import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import execEnvsHelper from "../../API/ExecutionEnvironments/execEnvsHelper";
import authorisationsHelper from "../../API/Authorisations/authorisationsHelper";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../Router";
import ExecEnvsSettings from "../Settings/Components/Execution Environments/ExecEnvsSettings";
import ToolTestPanel from "../Settings/Components/SubComponents/ToolTestPanel";
import AgentBuilderSidebar from "../Agents/Components/AgentBuilderSidebar";

const HASH_ROUTED_TABS = ["configure", "test"];
const HASH_ROUTED_SECTIONS = [
  "profile",
  "environment",
  "code",
  "resources",
  "behaviour",
  "usage",
  "versions",
];

const DEFAULT_HOOK_VALUES = {
  name: "",
  description: "",
  functionName: "",
  availableLibraries: "",
  customExecutionInstructions: "",
  customExecutionCode: "",
  customCodeExecutionEnvironmentVersion: "0.1.0",
  allowExternalAPI: false,
  hardcodedScript: false,
  isTemplate: false,
  staticDocs: null,
  staticArtifacts: null,
};

const HookBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const hookId = params?.hookId;
  const isEditMode = !!hookId && hookId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeTab, setActiveTab] = useState("configure");
  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isHooksLoading, setIsHooksLoading] = useState(true);
  const [availableAuthorisations, setAvailableAuthorisations] = useState([]);
  const [isAuthorisationsLoaded, setIsAuthorisationsLoaded] = useState(false);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const selectedHook = useMemo(
    () => state?.execEnvs?.find((u) => u?.id === hookId),
    [state?.execEnvs, hookId]
  );

  const loadHooks = useCallback(
    async (workspaceID) => {
      try {
        setIsHooksLoading(true);
        const execEnvsData =
          await execEnvsHelper.getCustomCodeExecutionEnvironments(
            workspaceID,
            null
          );
        const execEnvs =
          execEnvsData?.data?.customCodeExecutionEnvironmentsByWorkspace?.items?.filter(
            (u) => u?._deleted !== true
          ) || [];
        dispatch("SET_EXEC_ENVS", execEnvs);
      } catch (error) {
        console.error(error);
      } finally {
        setIsHooksLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const hooks = state?.execEnvs || [];
    const belongsToWorkspace = hooks.some((u) => u?.workspaceID === wsId);
    if (hooks.length === 0 || !belongsToWorkspace) {
      loadHooks(wsId);
    } else {
      setIsHooksLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    const fetchAuthorisations = async () => {
      const wsId = getActiveWorkspace()?.id;
      if (!wsId) return;
      setIsAuthorisationsLoaded(false);
      try {
        const authorisations = await authorisationsHelper.getAuthorisations(
          wsId
        );
        const _auth =
          authorisations?.data?.authorisationsByWorkspace?.items?.filter(
            (u) => u?._deleted != true
          ) || [];
        setAvailableAuthorisations(_auth);
      } catch (error) {
        console.error("Error fetching authorisations:", error);
        setAvailableAuthorisations([]);
      } finally {
        setIsAuthorisationsLoaded(true);
      }
    };
    fetchAuthorisations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!hookId) {
      history.replace(`/workspaces/${workspaceId}/settings/exec_envs`);
    }
  }, [hookId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (hookId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues({ ...DEFAULT_HOOK_VALUES });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isHooksLoading) return;
    if (editInitRef.current === hookId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const hook = (state?.execEnvs || []).find((u) => u?.id === hookId);
    if (!hook) {
      const listIsStale = state?.execEnvs?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/exec_envs`);
      return;
    }
    editInitRef.current = hookId;
    setInitialMapValues({ ...hook });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hookId,
    isEditMode,
    isHooksLoading,
    state?.execEnvs,
    getActiveWorkspace()?.id,
  ]);

  const isEnterprise = getActiveWorkspace()?.subscriptionType === "enterprise";

  const isSectionVisible = (sectionId) => {
    switch (sectionId) {
      case "environment":
        return isEnterprise;
      case "usage":
      case "versions":
        return isEditMode;
      default:
        return true;
    }
  };

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("hooks.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("hooks.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "environment",
            title: t("hooks.builderSectionEnvironment", {
              defaultValue: "Execution environment",
            }),
            icon: AppAssets.icons.terminal,
          },
          {
            id: "code",
            title: t("hooks.builderSectionCode", { defaultValue: "Code" }),
            icon: AppAssets.icons.code,
          },
          {
            id: "resources",
            title: t("hooks.builderSectionResources", {
              defaultValue: "Static resources",
            }),
            icon: AppAssets.icons.collection,
          },
          {
            id: "behaviour",
            title: t("hooks.builderSectionBehaviour", {
              defaultValue: "Behaviour",
            }),
            icon: AppAssets.icons.bolt,
          },
        ],
      },
      {
        id: "system",
        label: t("hooks.builderGroupSystem", { defaultValue: "System" }),
        items: [
          {
            id: "usage",
            title: t("hooks.builderSectionUsage", { defaultValue: "Usage" }),
            icon: AppAssets.icons.taskAgent,
          },
          {
            id: "versions",
            title: t("hooks.builderSectionVersions", {
              defaultValue: "Versions",
            }),
            icon: AppAssets.icons.history,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isEnterprise, t]);

  useEffect(() => {
    if (activeTab === "configure" && !isSectionVisible(activeSection)) {
      setActiveSection("profile");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, initialMapValues]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_TABS.includes(raw)) {
        setActiveTab(raw);
        return;
      }
      if (HASH_ROUTED_SECTIONS.includes(raw) && isSectionVisible(raw)) {
        setActiveTab("configure");
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/hooks/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const builderActiveSection = activeTab === "configure" ? activeSection : null;

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveTab("configure");
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("hooks.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeTab === "test" ? "test" : builderActiveSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builderActiveSection, activeTab]);

  // In edit mode, wait until the loaded values match the route id (see tools).
  const isFormReady =
    initialMapValues != null &&
    !isHooksLoading &&
    (!isEditMode || initialMapValues?.id === hookId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [builderActiveSection]);

  const handleExecEnvCreated = (status, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("execEnvCreationSuccessCaption"), { type: "success" });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEditMode && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/hooks/${savedId}`);
      }
    } else {
      AppToasty.show(t("execEnvgCreationFailedCaption"), { type: "danger" });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const tabs = [
    {
      id: "configure",
      label: t("hooks.builderConfigureTab", { defaultValue: "Configure" }),
      icon: AppAssets.icons.terminal,
    },
    {
      id: "test",
      label: t("hooks.builderTestTab", { defaultValue: "Test" }),
      icon: AppAssets.icons.launch,
    },
  ];

  const renderTabButton = (tab) => {
    const isActive = activeTab === tab.id;
    return (
      <TouchableOpacity
        key={tab.id}
        onPress={() => setActiveTab(tab.id)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: 7,
          paddingHorizontal: 16,
          borderRadius: 20,
          backgroundColor: isActive
            ? currentTheme?.theme?.backgroundColor
            : currentTheme?.theme?.transparent,
          borderWidth: 1,
          borderColor: isActive
            ? currentTheme?.theme?.lightGrey
            : currentTheme?.theme?.transparent,
        }}
      >
        {tab.icon && (
          <AppIcon
            icon={tab.icon}
            size={13}
            color={
              isActive
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.darkGrey
            }
            style={{ marginRight: 7 }}
          />
        )}
        <AppText
          style={{ fontSize: 13 }}
          fontWeight={isActive ? "600" : "normal"}
          color={
            isActive
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.darkGrey
          }
        >
          {tab.label}
        </AppText>
      </TouchableOpacity>
    );
  };

  const renderSaveFirstHint = () => (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
      }}
    >
      <AppIcon
        icon={AppAssets.icons.terminal}
        size={26}
        color={currentTheme?.theme?.mediumGrey}
      />
      <AppText
        style={{ marginTop: 12, textAlign: "center" }}
        color={currentTheme?.theme?.mediumGrey}
      >
        {t("hooks.builderSaveFirst", {
          defaultValue: "Create the hook first to unlock this section",
        })}
      </AppText>
    </View>
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingExecEnvs")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        {activeTab === "configure" && (
          <AgentBuilderSidebar
            groups={sectionGroups}
            activeSection={activeSection}
            onSelect={setActiveSection}
            errorSections={errorSections}
          />
        )}
        <ScrollView
          ref={scrollRef}
          style={{
            flex: 1,
            minWidth: 0,
            display: activeTab === "test" ? "none" : "flex",
          }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <ExecEnvsSettings
            key={hookId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? hookId : undefined}
            opt={isEditMode ? selectedHook : undefined}
            settingType="hook"
            onExecEnvCreated={handleExecEnvCreated}
            onExecEnvDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/exec_envs`)
            }
            availableAuthorisations={availableAuthorisations}
            isAuthorisationsLoaded={isAuthorisationsLoaded}
            isBuilderLayout
            builderActiveSection={builderActiveSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
        <View
          style={{
            flex: 1,
            minWidth: 0,
            display: activeTab === "test" ? "flex" : "none",
          }}
        >
          {isEditMode ? (
            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{ flexGrow: 1 }}
              showsVerticalScrollIndicator
            >
              <ToolTestPanel
                key={hookId}
                mode="hook"
                id={hookId}
                token={getActiveWorkspace()?.workspaceToken}
              />
            </ScrollView>
          ) : (
            renderSaveFirstHint()
          )}
        </View>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/exec_envs`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.hooks")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || selectedHook?.name || ""
            : t("hooks.builderNewHook", { defaultValue: "New hook" })}
        </AppText>
        <View
          style={{
            flex: 1,
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: currentTheme?.theme?.lightGrey,
              borderRadius: 24,
              padding: 4,
              gap: 2,
            }}
          >
            {tabs.map(renderTabButton)}
          </View>
        </View>
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("hooks.unsavedChangesSubCaption", {
          defaultValue:
            "This hook has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default HookBuilderScreenContainer;
